import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const InputField = ({ label, name, type = "text", value, onChange }) => (
  <div className="col-md-4">
    <label className="form-label">{label}</label>
    <input
      type={type}
      className="form-control"
      name={name}
      value={value}
      onChange={onChange}
      required
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options }) => (
  <div className="col-md-4">
    <label className="form-label">{label}</label>
    <select
      className="form-control"
      name={name}
      value={value}
      onChange={onChange}
      required
    >
      <option value="">Select...</option>
      {options.map((option, index) => (
        <option key={index} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

const ExaminationMarksUpdate = () => {
  const navigate = new useNavigate();
  const { exam_name, class_number, student_id } = useParams();
  const isUnitTest = exam_name.startsWith("unit");
  const isTerminal = exam_name.startsWith("terminal");
  const [student, setStudent] = useState({});
  const [marks, setMarks] = useState({
    mathematics: 0,
    science: 0,
    social_science: 0,
    computer: 0,
    english: 0,
    hindi: 0,
  }); // Holds marks for each subject

  // Define subjects for the exam
  const subjects = [
    "mathematics",
    "science",
    "social_science",
    "computer",
    "english",
    "hindi",
  ];

  useEffect(() => {
    // window.scrollTo({ top: 450, behavior: "smooth" });
    // Fetch student data to pre-fill the form
    const fetchStudentData = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/student/fetch/${student_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          setStudent(response.data); // Set the form data with the fetched student details
        }
      } catch (error) {
        toast.error("An error occurred while fetching student data.");
      }
    };
    const fetchMarks = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/marks/fetch/${exam_name}/${student_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          setMarks({
            mathematics: response.data.marks?.mathematics || 0,
            science: response.data.marks?.science || 0,
            social_science: response.data.marks?.social_science || 0,
            computer: response.data.marks?.computer || 0,
            english: response.data.marks?.english || 0,
            hindi: response.data.marks?.hindi || 0,
          });
          // window.scrollTo({ top: 450, behavior: "smooth" });
          toast.success(response.data.message);
        } else {
          toast.error("Failed to fetch marks.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching marks.");
      }
    };

    fetchStudentData();
    fetchMarks();
  }, [student_id, exam_name]);

  // Function to handle marks changes
  const handleMarksChange = (e, subject) => {
    const { value } = e.target;
    setMarks((prevMarks) => ({
      ...prevMarks,
      [subject]: parseInt(value) || 0,
    }));
  };

  // Calculate total marks
  const calculateTotalMarks = () => {
    return Object.values(marks).reduce((acc, mark) => acc + mark, 0);
  };

  // Calculate percentage
  const calculatePercentage = () => {
    const totalObtained = calculateTotalMarks();
    const totalMarks = subjects.length * 100; // Total marks for all subjects
    return ((totalObtained / totalMarks) * 100).toFixed(2); // Calculate percentage
  };

  // Handle form submission for marks update
  const handleMarksUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/marks/update/${exam_name}/${student_id}`,
        { marks: marks },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        window.scrollTo({ top: 450, behavior: "smooth" });
        toast.success(response.data.message);
        setTimeout(() => {
          navigate(`/erp/examinations/${exam_name}/class/${class_number}`);
        }, 2000);
      } else {
        toast.error("Failed to update marks.");
      }
    } catch (error) {
      toast.error("An error occurred while updating marks.");
    }
  };

  return (
    <>
      <form
        className="container bg-white mt-5 rounded p-5 mb-5"
        onSubmit={handleMarksUpdate}
      >
        {/* Back Button */}
        <div className="row">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <Link
              to={`/erp/examinations/${exam_name}/class/${class_number}`}
              className="btn btn-secondary mb-3"
            >
              &#8592; Back to Class List
            </Link>
            <h3 className="text-center text-success d-flex justify-content-between align-items-start ">
              Student Marksheet
            </h3>
          </div>
          <div className="text-center d-flex justify-content-end">
            <h5>Student ID - {student_id}</h5>
          </div>
        </div>

        <div className="table-responsive mt-3">
          <table className="table table-bordered table-striped table-hover align-middle">
            <thead className="table-primary">
              <tr>
                <th style={{ width: "45%" }}>Field</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries({
                "Student Name": student.student_name,
                "Class Roll No.": student.class_roll_number
                  ? student.class_roll_number
                  : " ",
                Class: student.student_class,
                Session: student.session,
                "Exam Name":
                  exam_name === "unit-test-1"
                    ? "Unit Test 1"
                    : exam_name === "terminal-1"
                    ? "Terminal 1"
                    : exam_name === "unit-test-2"
                    ? "Unit Test 2"
                    : exam_name === "terminal-2"
                    ? "Terminal 2"
                    : exam_name === "unit-test-3"
                    ? "Unit Test 3"
                    : "Terminal 3",
              }).map(([key, value]) => (
                <tr key={key}>
                  <td className="fw-bold">{key}</td>
                  <td>{value || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <table className="table table-secondary text-center mt-3">
          <thead>
            <tr>
              <th scope="col">S. No.</th>
              <th scope="col">Subject</th>
              <th scope="col">Marks Obtained</th>
              <th scope="col">Total Marks</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((subject, index) => (
              <tr key={index}>
                <th scope="row">{index + 1}</th>
                <td>
                  {subject === "mathematics"
                    ? "Mathematics"
                    : subject === "science"
                    ? "Science"
                    : subject === "english"
                    ? "English"
                    : subject === "social_science"
                    ? "Social Science"
                    : subject === "hindi"
                    ? "Hindi"
                    : "Computer"}
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={marks[subject] || ""}
                    onChange={(e) =>
                      setMarks((prevMarks) => ({
                        ...prevMarks,
                        [subject]: parseInt(e.target.value) || 0, // Save marks directly in state
                      }))
                    }
                    placeholder={`Enter marks`}
                    max={isUnitTest ? "20" : "80"}
                    required
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={isUnitTest ? "20" : "80"} // Dynamic total marks per subject
                    readOnly
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="container mt-3">
          <h5>Total Marks Obtained: {calculateTotalMarks()}</h5>
          {/* <h5>Percentage: {calculatePercentage()}%</h5> */}
        </div>

        <div className="d-grid gap-2 d-flex justify-content-center mt-4">
          <button type="submit" className="btn btn-primary">
            Update Marks
          </button>
        </div>
      </form>

      <ToastContainer />
    </>
  );
};

export default ExaminationMarksUpdate;
